import {
	CustomerInListDTO,
	GetCustomersByTextRequest,
	GetCustomersByTextResponse,
	GetCustomerDetailsByIdRequest,
	GetCustomerDetailsByIdResponse,
	RecentSaleDTO,
	GetRecentSalesByCustomerResponse,
	GetRecentSalesByCustomerRequest,
	CustomerDetailsDTO,
	BuyerCustomerDTO,
	CustomerForEditionDTO,
	EditCustomerRequest,
	EditCustomerResponse,
	CreateCustomerRequest,
	CreateCustomerResponse,
	SaveCustomerRequest,
	GetPossibleDuplicateCustomersRequest,
	GetPossibleDuplicateCustomersResponse,
	DeleteCustomerRequest,
	ValidateCustomerRequest,
	ValidateCustomerResponse,
	GetCustomerByPhoneRequest,
	GetCustomerByPhoneResponse,
	AddDeliveryAddressRequest,
	AddressDTO,
	RemoveDeliveryAddressRequest,
	GetCustomerDetailsByCardNumberRequest,
	GetCustomerDetailsByCardNumberResponse,
	SaveCustomerResponse
} from 'core/api';
import { Bus, SessionProvider, ValidationErrors } from 'core/infrastructure';

const process = Bus.buildProcessFn('/bus/');

export function getByText(criteria: string, maxResults: Nullable<number>, filterByCustomerTypeId: Nullable<number>): Promise<CustomerInListDTO[]> {
	const posId = SessionProvider.getCurrent().PosId;
	const request = new GetCustomersByTextRequest({ criteria, posId, maxResults, filterByCustomerTypeId });
	return process(request, (response: GetCustomersByTextResponse) => response.Customers);
}

export function getDetailsByCardNumber(cardNumber: string): Promise<CustomerDetailsDTO> {
	const request = new GetCustomerDetailsByCardNumberRequest({ cardNumber });
	return process(request, (response: GetCustomerDetailsByCardNumberResponse) => response.Customer);
}

export function getByPhone(phone: string): Promise<CustomerForEditionDTO> {
	const request = new GetCustomerByPhoneRequest({ phone });
	return process(request, (response: GetCustomerByPhoneResponse) => response.Customer);
}

export async function getDetails(customerId: number): Promise<CustomerDetailsDTO> {
	const request = new GetCustomerDetailsByIdRequest({ customerId });
	return await process(request, (response: GetCustomerDetailsByIdResponse) => response.Customer);
}

export async function getUpdatedDetails(customer: BuyerCustomerDTO): Promise<CustomerDetailsDTO> {

	const existing = await getDetails(customer.Id);

	existing.Address = customer.Address;
	existing.ApplySurcharge = customer.ApplySurcharge;
	existing.BusinessName = customer.BusinessName;
	existing.Cif = customer.Cif;
	existing.FiscalName = customer.FiscalName;
	existing.DiscountRate = customer.DiscountRate;

	return existing;
}

export async function getRecentSales(customerId: number): Promise<RecentSaleDTO[]> {
	const request = new GetRecentSalesByCustomerRequest({ customerId });
	return await process(request, (response: GetRecentSalesByCustomerResponse) => response.RecentSales);
}

export async function getForEdition(customerId: number): Promise<CustomerForEditionDTO> {
	const request = new EditCustomerRequest({ id: customerId });
	return await process(request, (response: EditCustomerResponse) => response.Data);
}

export async function create(): Promise<CustomerForEditionDTO> {
	const request = new CreateCustomerRequest();
	return await process(request, (response: CreateCustomerResponse) => response.Data);
}

export async function save(customer: CustomerForEditionDTO): Promise<CustomerForEditionDTO> {
	const request = new SaveCustomerRequest({ data: customer });
	return await process(request, (response: SaveCustomerResponse) => response.Data);
}

export async function validate(customer: CustomerForEditionDTO): Promise<ValidationErrors> {
	const request = new ValidateCustomerRequest({ data: customer });
	return await process(request, (response: ValidateCustomerResponse) => {
		const errors: ValidationErrors = {};
		response.Notification.Messages.forEach(m => {
			errors[m.PropertyName] = errors[m.PropertyName] || [];
			errors[m.PropertyName].push(m.MessageText);
		});
		return errors;
	});
}

export async function getPossibleDuplicates(customer: CustomerForEditionDTO): Promise<CustomerInListDTO[]> {
	const request = new GetPossibleDuplicateCustomersRequest({ customer });
	return await process(request, (response: GetPossibleDuplicateCustomersResponse) => response.Customers);
}

export async function deleteCustomer(customerId: number): Promise<void> {
	const request = new DeleteCustomerRequest({ id: customerId });
	await process(request);
}

export async function addDeliveryAddress(customerId: number, address: AddressDTO): Promise<void> {
	const request = new AddDeliveryAddressRequest({ customerId, address });
	await process(request);
}

export async function removeDeliveryAddress(customerId: number, address: AddressDTO) {
	const request = new RemoveDeliveryAddressRequest({ customerId, address });
	await process(request);
}

export async function getDeliveryAddresses(customerId: number): Promise<AddressDTO[]> {
	const customer = await getForEdition(customerId);
	return customer.DeliveryAddresses;
}
